// Персист кэша словарей фильтров в localStorage, per-user, на этом устройстве
// (тот же посыл, что и viewer/persistentCache.ts). Позволяет после перезагрузки страницы
// показать сайдбар и строку тегов мгновенно, без спиннеров, и обновить их в фоне.

import { DICTIONARY_NAMES, DictionaryCache, FiltersCacheShape } from './model';

const STORAGE_PREFIX = 'assetLab.filtersCache';
// Версия схемы: при несовместимом изменении формы записей — поднять, чтобы старые
// записи из localStorage игнорировались.
const VERSION = 1;
// 7 дней. Отсчёт идёт от момента получения данных (storedAt записи), а не от последнего
// визита в приложение, поэтому TTL действительно истекает. Словари меняются редко, и в них
// нет ни ссылок, ни имён файлов — держать их дольше, чем списки, безопасно.
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
// Аномально большой словарь (например, каталог с тысячами категорий) не персистим:
// обрезать его нельзя — у дерева порвутся связи с родителями, а класть целиком значит
// рисковать квотой. В памяти такой словарь кэшируется как обычно.
const MAX_ITEMS_PER_ENTRY = 2000;

type StoredEntry = { items: unknown[]; savedAt: number };

type StoredPayload = {
  version: number;
  dicts: Partial<Record<string, Record<string, StoredEntry>>>;
};

function keyFor(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

function isStoredEntry(value: unknown): value is StoredEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return typeof entry.savedAt === 'number' && Array.isArray(entry.items);
}

// Читает кэш словарей пользователя. Отбрасывает протухшие по TTL записи. Каждая валидная
// запись помечается revalidate: true — при первом обращении она будет тихо обновлена из сети.
// Возвращает {} если хранилище недоступно, версия иная или ничего валидного нет.
export function loadPersistedFiltersCache(userId: string): Partial<FiltersCacheShape> {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const payload = parsed as Partial<StoredPayload>;
    if (payload.version !== VERSION || !payload.dicts || typeof payload.dicts !== 'object') {
      return {};
    }

    const now = Date.now();
    // Собираем в нетипизированную форму: dict здесь — переменная из union, и TS требовал бы
    // от значения пересечения всех четырёх типов словарей. Соответствие dict → тип элементов
    // обеспечено разбором по DICTIONARY_NAMES и проверяется на записи (StoredPayload).
    const result: Partial<Record<string, DictionaryCache<unknown>>> = {};
    DICTIONARY_NAMES.forEach((dict) => {
      const entries = payload.dicts?.[dict];
      if (!entries || typeof entries !== 'object') {
        return;
      }
      const cache: DictionaryCache<unknown> = {};
      Object.entries(entries).forEach(([key, entry]) => {
        if (!isStoredEntry(entry)) return;
        if (now - entry.savedAt > TTL_MS) return;
        // storedAt — настоящее время получения данных, поэтому TTL честный и после гидрации.
        cache[key] = { items: entry.items, storedAt: entry.savedAt, revalidate: true };
      });
      if (Object.keys(cache).length > 0) {
        result[dict] = cache;
      }
    });
    return result as Partial<FiltersCacheShape>;
  } catch {
    return {};
  }
}

// Сохраняет кэш словарей пользователя. Число записей на словарь ограничивать здесь не
// нужно — предел держит редьюсер (MAX_ENTRIES_PER_DICTIONARY), в кэш больше и не попадёт.
export function savePersistedFiltersCache(userId: string, cache: FiltersCacheShape): void {
  try {
    const dicts: StoredPayload['dicts'] = {};

    DICTIONARY_NAMES.forEach((dict) => {
      const source = cache[dict] as DictionaryCache<unknown>;
      const entries: Record<string, StoredEntry> = {};
      Object.entries(source).forEach(([key, entry]) => {
        if (entry.items.length > MAX_ITEMS_PER_ENTRY) {
          return;
        }
        // Сохраняем время получения данных, а не время записи в хранилище: иначе TTL
        // отсчитывался бы от последнего визита в приложение и не истекал никогда.
        entries[key] = { items: entry.items, savedAt: entry.storedAt ?? Date.now() };
      });

      if (Object.keys(entries).length > 0) {
        dicts[dict] = entries;
      }
    });

    const payload: StoredPayload = { version: VERSION, dicts };
    localStorage.setItem(keyFor(userId), JSON.stringify(payload));
  } catch {
    // Хранилище недоступно или переполнено (quota) — молча пропускаем: кэш остаётся
    // только в памяти на текущую сессию. Словари маленькие: если квота кончилась, дело
    // почти наверняка в кэше списков, там переполнение и сообщается (viewer/persistentCache).
  }
}

// Удаляет кэши словарей других пользователей этого устройства (логаута в SPA нет, см.
// dropForeignViewerCaches).
export function dropForeignFiltersCaches(userId: string): void {
  try {
    const own = keyFor(userId);
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}.`) && key !== own) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Хранилище недоступно — чистить нечего.
  }
}
